/**
 * Karr Offline Database — Dexie.js IndexedDB wrapper.
 *
 * Mirrors key Supabase tables locally for offline-first UX.
 * Tables stored:
 *   - transactions (invoices, bills, receipts, payments, etc.)
 *   - journal_entries (auto-generated from transactions)
 *   - contacts (customers, vendors, employees)
 *   - items (products, services)
 *   - accounts (chart of accounts)
 *   - sync_queue (pending writes to flush when online)
 *
 * @since 1.0.0
 */
import Dexie, { type Table } from 'dexie';

// ─── Local Table Interfaces ──────────────────────────────────

export interface OfflineTransaction {
    id: string;
    org_id: string;
    type: string;
    sub_type?: string;
    number?: string;
    date: string;
    contact_id?: string;
    contact_name?: string;
    total: number;
    tax_amount?: number;
    currency: string;
    status: string;
    narration?: string;
    created_at: string;
    updated_at: string;
    /** Supabase server version — used for conflict detection */
    _server_version?: number;
    /** Local-only flag: true if this record has unsynced changes */
    _dirty?: boolean;
}

export interface OfflineJournalEntry {
    id: string;
    org_id: string;
    transaction_id: string;
    account_id: string;
    account_name?: string;
    debit: number;
    credit: number;
    narration?: string;
    created_at: string;
}

export interface OfflineContact {
    id: string;
    org_id: string;
    name: string;
    type: string; // customer | vendor | both | employee
    email?: string;
    phone?: string;
    gstin?: string;
    pan?: string;
    city?: string;
    state_code?: string;
    outstanding_receivable?: number;
    outstanding_payable?: number;
    created_at: string;
    updated_at: string;
    _server_version?: number;
    _dirty?: boolean;
}

export interface OfflineItem {
    id: string;
    org_id: string;
    name: string;
    type: string; // product | service
    sku?: string;
    hsn_code?: string;
    unit?: string;
    sale_price?: number;
    purchase_price?: number;
    tax_rate?: number;
    created_at: string;
    updated_at: string;
    _server_version?: number;
    _dirty?: boolean;
}

export interface OfflineAccount {
    id: string;
    org_id: string;
    code?: string;
    name: string;
    account_type: string; // asset | liability | equity | revenue | expense
    sub_type?: string;
    parent_id?: string;
    fs_line?: string;
    is_system: boolean;
    created_at: string;
    _server_version?: number;
}

export interface SyncQueueEntry {
    /** Auto-increment primary key */
    id?: number;
    /** Supabase table name */
    table: string;
    /** insert | update | delete */
    operation: 'insert' | 'update' | 'delete';
    /** The record ID */
    record_id: string;
    /** The data payload (null for delete) */
    payload: Record<string, unknown> | null;
    /** Organization context */
    org_id: string;
    /** When the operation was queued */
    queued_at: string;
    /** Number of retry attempts */
    retries: number;
    /** Last error message (if failed) */
    last_error?: string;
    /** Status: pending | processing | failed | done */
    status: 'pending' | 'processing' | 'failed' | 'done';
}

/**
 * AI Memory — offline cache of vendor→account mappings.
 * Mirrors the Supabase `ai_memory` table for offline-first AI suggestions.
 *
 * @since 2.0.0 — Added for KarrAI learning system
 */
export interface OfflineAiMemory {
    id: string;
    org_id: string;
    vendor_raw: string;
    vendor_normalized: string;
    account_id: string;
    account_name: string;
    confidence: number;
    use_count: number;
    last_used_at: string;
    aliases: string[];
    created_at: string;
    updated_at: string;
}

// ─── Dexie Database ──────────────────────────────────────────

class KarrOfflineDB extends Dexie {
    transactions!: Table<OfflineTransaction, string>;
    journal_entries!: Table<OfflineJournalEntry, string>;
    contacts!: Table<OfflineContact, string>;
    items!: Table<OfflineItem, string>;
    accounts!: Table<OfflineAccount, string>;
    sync_queue!: Table<SyncQueueEntry, number>;
    ai_memory!: Table<OfflineAiMemory, string>;

    constructor() {
        super('KarrOfflineDB');

        this.version(1).stores({
            // Primary key is `id`, indexed by org_id, type, date, status, contact_id
            transactions: 'id, org_id, type, date, status, contact_id, updated_at',
            // Primary key is `id`, indexed by org_id, transaction_id, account_id
            journal_entries: 'id, org_id, transaction_id, account_id',
            // Primary key is `id`, indexed by org_id, type, name
            contacts: 'id, org_id, type, name',
            // Primary key is `id`, indexed by org_id, type
            items: 'id, org_id, type, name',
            // Primary key is `id`, indexed by org_id, account_type, parent_id
            accounts: 'id, org_id, account_type, parent_id',
            // Auto-increment primary key, indexed by status, table
            sync_queue: '++id, status, table, org_id, queued_at',
        });

        // v2: Add AI memory table for offline vendor→account lookups
        this.version(2).stores({
            ai_memory: 'id, org_id, vendor_normalized, confidence, updated_at',
        });
    }
}

// Singleton instance
export const offlineDb = new KarrOfflineDB();

// ─── Utility: Clear all data for an org ──────────────────────

export async function clearOrgData(orgId: string): Promise<void> {
    await offlineDb.transaction(
        'rw',
        [
            offlineDb.transactions,
            offlineDb.journal_entries,
            offlineDb.contacts,
            offlineDb.items,
            offlineDb.accounts,
            offlineDb.ai_memory,
        ],
        async () => {
            await offlineDb.transactions.where('org_id').equals(orgId).delete();
            await offlineDb.journal_entries.where('org_id').equals(orgId).delete();
            await offlineDb.contacts.where('org_id').equals(orgId).delete();
            await offlineDb.items.where('org_id').equals(orgId).delete();
            await offlineDb.accounts.where('org_id').equals(orgId).delete();
            await offlineDb.ai_memory.where('org_id').equals(orgId).delete();
        }
    );
}

// ─── Utility: Get queue count for an org ─────────────────────

export async function getPendingCount(orgId?: string): Promise<number> {
    if (orgId) {
        return offlineDb.sync_queue
            .where({ status: 'pending', org_id: orgId })
            .count();
    }
    return offlineDb.sync_queue.where('status').equals('pending').count();
}
